import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import "../styles/pages/daily-highlights.scss";

const NHL_PLAYER_BASE = "https://players.brightcove.net/6415718365001/EXtG1xJ7H_default/index.html";

const getYesterday = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const cleanTitle = (title) => title.replace(/^[A-Z]{2,3}@[A-Z]{2,3}:\s*/, "");

const stateLabel = (state) => (["OFF", "FINAL"].includes(state) ? "Final" : "In progress");

const ClipList = ({ label, clips, onPlay, activeId }) => {
  if (!clips.length) return null;
  return (
    <details className="daily-highlights-clips">
      <summary>{label} ({clips.length})</summary>
      <div className="daily-highlights-clips__list">
        {clips.map((clip) => (
          <button
            key={clip.clipId}
            type="button"
            className={`daily-highlights-chip ${activeId === clip.clipId ? "is-active" : ""}`}
            onClick={() => onPlay({ ...clip, title: cleanTitle(clip.title) })}
          >
            {cleanTitle(clip.title)}
          </button>
        ))}
      </div>
    </details>
  );
};

const GameCard = ({ game, onPlay, activeId }) => {
  const hasClips = game.recap || game.condensed || game.goals.length || game.saves.length;
  return (
    <article className="daily-highlights-card">
      <header className="daily-highlights-card__teams">
        {[game.away, game.home].map((team, index) => (
          <div className="daily-highlights-team" key={team.abbrev}>
            {team.logo && <img src={team.logo} alt="" />}
            <strong>{team.abbrev}</strong>
            <span className="daily-highlights-team__score">{team.score ?? "–"}</span>
            {index === 0 && <span className="daily-highlights-card__at" aria-hidden="true">@</span>}
          </div>
        ))}
        <span className="daily-highlights-card__state">{stateLabel(game.state)}</span>
      </header>

      {hasClips ? (
        <>
          <div className="daily-highlights-card__main">
            {game.recap && (
              <button type="button" className="fb-btn fb-btn--primary" onClick={() => onPlay({ ...game.recap, title: `${game.away.abbrev} at ${game.home.abbrev} · Recap` })}>
                Recap
              </button>
            )}
            {game.condensed && (
              <button type="button" className="fb-btn fb-btn--ghost" onClick={() => onPlay({ ...game.condensed, title: `${game.away.abbrev} at ${game.home.abbrev} · Condensed game` })}>
                Condensed game
              </button>
            )}
          </div>
          <ClipList label="Goals" clips={game.goals} onPlay={onPlay} activeId={activeId} />
          <ClipList label="Saves" clips={game.saves} onPlay={onPlay} activeId={activeId} />
        </>
      ) : (
        <p className="daily-highlights-card__empty">Highlights haven't been posted for this game yet.</p>
      )}
    </article>
  );
};

const VIEWS = [
  { key: "yesterday", label: "Yesterday" },
  { key: "mercer", label: "Dawson Mercer" },
  { key: "newhook", label: "Alex Newhook" },
];

const ClipRow = ({ clip, onPlay, active }) => (
  <li>
    <button
      type="button"
      className={`daily-highlights-row ${active ? "is-active" : ""}`}
      onClick={() => onPlay({ ...clip, title: cleanTitle(clip.title) })}
    >
      <span className="daily-highlights-row__play" aria-hidden="true">▶</span>
      <span className="daily-highlights-row__main">
        <strong>{cleanTitle(clip.title)}</strong>
        <small>{new Date(clip.date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })} · {clip.season}</small>
      </span>
      {clip.badges.length > 0 && (
        <span className="daily-highlights-row__badges">
          {clip.badges.map((badge) => <span className="daily-highlights-badge" key={badge}>{badge}</span>)}
        </span>
      )}
    </button>
  </li>
);

const DailyHighlights = () => {
  const [date] = useState(getYesterday);
  const [view, setView] = useState("yesterday");
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searched, setSearched] = useState(null);
  const [season, setSeason] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const playerRef = useRef(null);
  const cacheRef = useRef({});
  const activeViewRef = useRef(view);
  activeViewRef.current = view;

  const load = useCallback(async (force = false) => {
    setError("");
    if (!force && cacheRef.current[view]) {
      setData(cacheRef.current[view]);
      setLoading(false);
      return;
    }
    setData(null);
    setLoading(true);
    const isYesterday = view === "yesterday";
    const isSearched = view.startsWith("p:");
    try {
      const { data: result, error: invokeError } = await supabase.functions.invoke(
        isYesterday ? "nhl-daily-highlights" : "nhl-player-highlights",
        { body: isYesterday ? { date } : isSearched ? { playerId: view.slice(2) } : { player: view } }
      );
      if (invokeError || result?.error) throw invokeError || new Error(result.error);
      cacheRef.current[view] = result;
      if (activeViewRef.current === view) setData(result);
    } catch (loadError) {
      console.error("Unable to load highlights:", loadError);
      if (activeViewRef.current === view) setError("Could not load highlights. Please try again.");
    } finally {
      if (activeViewRef.current === view) setLoading(false);
    }
  }, [date, view]);

  useEffect(() => {
    setSelected(null);
    setSeason("all");
    load();
  }, [load]);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      setSearchError("");
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const { data: found, error: invokeError } = await supabase.functions.invoke("nhl-player-highlights", {
          body: { action: "search", query: term },
        });
        if (invokeError || found?.error) throw invokeError || new Error(found.error);
        if (!cancelled) {
          setResults(found.players || []);
          setSearchError("");
        }
      } catch (searchFailure) {
        console.error("Player search failed:", searchFailure);
        if (!cancelled) setSearchError("Search isn't working right now. Please try again.");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  const chooseSearchedPlayer = (player) => {
    setSearched(player);
    setView(`p:${player.playerId}`);
    setQuery("");
    setResults([]);
  };

  useEffect(() => {
    if (selected) playerRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selected]);

  const prettyDate = new Date(`${date}T12:00:00`).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
  const validSelection = selected && /^\d+$/.test(selected.clipId) ? selected : null;
  const isYesterday = view === "yesterday";
  const isSearched = view.startsWith("p:");
  const playerName = data?.player?.name ?? (isSearched ? searched?.name : VIEWS.find((item) => item.key === view)?.label) ?? "Player";
  const games = isYesterday ? data?.games ?? [] : [];
  const clips = isYesterday ? [] : data?.clips ?? [];
  const seasons = [...new Set(clips.map((clip) => clip.season))];
  const visibleClips = season === "all" ? clips : clips.filter((clip) => clip.season === season);
  const hasContent = isYesterday ? games.length > 0 : clips.length > 0;

  return (
    <main className="fb-page daily-highlights-page">
      <section className="daily-highlights-panel fb-card">
        <header className="daily-highlights-header">
          <p className="fb-page__eyebrow">NHL</p>
          <h1>{isYesterday ? "Yesterday's Highlights" : `${playerName} · ${isSearched ? "Highlights" : "Career Highlights"}`}</h1>
          <p>{isYesterday ? prettyDate : data ? `${data.total} goal highlights${data.partial ? " · most recent shown" : ""}` : " "}</p>
        </header>

        <div className="daily-highlights-search">
          <input
            type="search"
            className="form-control"
            placeholder="Search any NHL player…"
            aria-label="Search NHL players"
            value={query}
            maxLength={40}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query.trim().length >= 2 && (
            <div className="daily-highlights-search__results" role="listbox" aria-label="Player results">
              {searching && <p className="daily-highlights-search__status">Searching…</p>}
              {!searching && searchError && <p className="daily-highlights-search__status text-danger">{searchError}</p>}
              {!searching && !searchError && results.length === 0 && (
                <p className="daily-highlights-search__status">No players found.</p>
              )}
              {!searching && !searchError && results.map((player) => (
                <button key={player.playerId} type="button" role="option" onClick={() => chooseSearchedPlayer(player)}>
                  <strong>{player.name}</strong>
                  <small>
                    {[player.position, player.team, player.active ? null : "Retired"].filter(Boolean).join(" · ")}
                  </small>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="daily-highlights-toggle" role="group" aria-label="Highlights to show">
          {VIEWS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={view === item.key ? "is-active" : ""}
              aria-pressed={view === item.key}
              onClick={() => setView(item.key)}
            >
              {item.label}
            </button>
          ))}
          {isSearched && searched && (
            <button type="button" className="is-active" aria-pressed="true">{searched.name}</button>
          )}
        </div>

        {validSelection && (
          <div className="daily-highlights-player" ref={playerRef}>
            <div className="daily-highlights-player__bar">
              <strong>{validSelection.title}</strong>
              <button type="button" className="btn-close" aria-label="Close video" onClick={() => setSelected(null)} />
            </div>
            <div className="daily-highlights-player__frame">
              <iframe
                title={validSelection.title}
                src={`${NHL_PLAYER_BASE}?videoId=${validSelection.clipId}`}
                allowFullScreen
                allow="autoplay; fullscreen; encrypted-media"
              />
            </div>
          </div>
        )}

        {error && <div className="alert alert-danger" role="alert">{error}</div>}

        {loading ? (
          <div className="daily-highlights-empty" role="status">Loading highlights…</div>
        ) : hasContent ? (
          isYesterday ? (
            <div className="daily-highlights-list">
              {games.map((game) => (
                <GameCard key={game.id} game={game} onPlay={setSelected} activeId={validSelection?.clipId} />
              ))}
            </div>
          ) : (
            <>
              <label className="daily-highlights-season">
                <span>Season</span>
                <select className="form-select" value={season} onChange={(event) => setSeason(event.target.value)}>
                  <option value="all">All seasons ({clips.length})</option>
                  {seasons.map((item) => (
                    <option key={item} value={item}>{item} ({clips.filter((clip) => clip.season === item).length})</option>
                  ))}
                </select>
              </label>
              {data?.partial && (
                <p className="daily-highlights-note">Showing this player's most recent highlights. Older clips may not be included.</p>
              )}
              <ul className="daily-highlights-reel">
                {visibleClips.map((clip) => (
                  <ClipRow key={clip.clipId} clip={clip} onPlay={setSelected} active={validSelection?.clipId === clip.clipId} />
                ))}
              </ul>
            </>
          )
        ) : (
          !error && (
            <div className="daily-highlights-empty">
              <span aria-hidden="true">🏒</span>
              <h2>{isYesterday ? "No games yesterday" : "No highlights found"}</h2>
              <p>{isYesterday ? "Check back after the next game day." : "The NHL hasn't published any for this player yet."}</p>
            </div>
          )
        )}

        <footer className="daily-highlights-footer">
          <span>{isYesterday ? "Official NHL highlights. Recaps are posted once games finish." : "Official NHL highlights, newest first."}</span>
          <button type="button" className="fb-btn fb-btn--ghost" onClick={() => load(true)} disabled={loading}>Refresh</button>
        </footer>
      </section>
    </main>
  );
};

export default DailyHighlights;
